import React, { useEffect, useState, lazy } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';

import { ShowMessengerParams } from './types/API/VirtualWaitingRoom';
import { RootDispatch, RootState } from './store';
import { determineAppType } from './util';

import { orgId, orgName } from './selectors/organization';
import { DotsIndicator } from './components/DotsIndicator';
import { trackVirtualWaitingRoomConversationLoad } from './services/Tracker';

const Messenger = lazy(() => import('./components/Messenger'));
const Intake = lazy(() => import('./VirtualWaitingRoom/Intake'));
const Rejoin = lazy(() => import('./VirtualWaitingRoom/Rejoin'));
const GenericTextScreen = lazy(() => import('./VirtualWaitingRoom/GenericTextScreen'));

type VirtualWaitingRoomProps = {
  showMessenger: (params: ShowMessengerParams) => Promise<void>;
  showSessionEnded: (isSessionEnded: boolean) => void;
  isSessionEnded: boolean;
  organizationId: string;
  organizationName: string;
};

function VirtualWaitingRoom({
  showMessenger,
  showSessionEnded,
  isSessionEnded = false,
  organizationId,
  organizationName,
}: VirtualWaitingRoomProps) {
  const [viewBasedOnUserState, setViewBasedOnUserState] = useState<JSX.Element | null>(null);
  const [isConv, setIsConv] = useState(false);
  const APP_TYPE = determineAppType();
  const intl = useIntl();

  useEffect(() => {
    if (isSessionEnded) {
      setViewBasedOnUserState(
        <GenericTextScreen
          headerText={intl.formatMessage({ id: 'virtualWaitingRoom.sessionEndedHeader' })}
          subHeaderText={intl.formatMessage({ id: 'virtualWaitingRoom.sessionEndedSubheader' })}
        />
      );
    } else if (isConv) {
      trackVirtualWaitingRoomConversationLoad(organizationName, organizationId);
      setViewBasedOnUserState(<Messenger />);
    } else if (APP_TYPE === 'virtualWaitingRoom') {
      setViewBasedOnUserState(<Intake showMessenger={showMessenger} setIsConv={setIsConv} />);
    } else if (APP_TYPE === 'virtualWaitingRoomVisit') {
      setViewBasedOnUserState(
        <Rejoin
          showMessenger={showMessenger}
          setIsConv={setIsConv}
          setSessionEnded={showSessionEnded}
        />
      );
    } else {
      setViewBasedOnUserState(<DotsIndicator color="#65659e" />);
    }
  }, [
    isConv,
    isSessionEnded,
    APP_TYPE,
    showMessenger,
    intl,
    showSessionEnded,
    organizationName,
    organizationId,
  ]);

  return viewBasedOnUserState;
}

const mapDispatch = ({
  virtualWaitingRoom: { showMessenger, showSessionEnded },
}: RootDispatch) => ({
  showMessenger,
  showSessionEnded,
});

const mapState = (state: RootState) => ({
  isSessionEnded: state.virtualWaitingRoom.isSessionEnded,
  organizationName: orgName(state),
  organizationId: orgId(state),
});

// https://tigertext.atlassian.net/browse/TMG-837
// @ts-expect-error
export default connect(mapState, mapDispatch)(VirtualWaitingRoom);
